import { useEffect, useState } from "react";
import { formatDate } from "../../../../../api/DateUtils";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import EpaCard from "../../../../../leap-components/EpaCard";
import BreadCrumbs from "./BreadCrumbs";
import Header from "./Header";
import { Grid } from "../../../../../leap-components/Grid";
import CircularEconomyHomeContainer from "./CircularEconomyHomeContainer";
import {
  GetCertificatesOfRegistrationFetchUrl,
  GetCertificatesOfRegistrationCsv,
} from "../../../../../api/CircularEconomy/CertificateOfRegistration/ICertificateOfRegistration";
import { EpaGridOrderByFetchParams } from "../../../../../leap-components/Grid/EpaGridProps";
import { CreateStaticSelectColumnFilter } from "../../../../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import {
  loadCertificateOfRegistrationFilterValuesAsync,
  selectCertificateOfRegistrationFilterValuesResponse,
} from "../../../redux/slices/certificateOfRegistrationList";
import useAppSelector from "../../../../../hooks/useAppSelector";
import useAppDispatch from "../../../../../hooks/useAppDispatch";

interface ISearchResultRow {
  licence_name: string;
  licence_number: string;
  organisation: string;
  town: string;
  county: string;
}

const GetDetailsUrl = (licenseId: string) => {
  return `/cor/${licenseId}`;
};

const CertificatesOfRegistrationList = () => {
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const certificateOfRegistrationListSelector = useAppSelector(
    selectCertificateOfRegistrationFilterValuesResponse
  );
  const dispatch = useAppDispatch();

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  if (!loading) {
    setLoading(true);
    dispatch(loadCertificateOfRegistrationFilterValuesAsync());
  }

  const statusFilterValues =
    certificateOfRegistrationListSelector.statusFilterValues;

  const localAuthorityFilterValues =
    certificateOfRegistrationListSelector.localAuthorityFilterValues;

  const wasteCategoryFilterValues =
    certificateOfRegistrationListSelector.wasteCategoryFilterValues;

  const classOfActivityFilterValues =
    certificateOfRegistrationListSelector.classOfActivityFilterValues;

  const tableColumnsBig: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Reg Number",
      accessor: "reg_no",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetDetailsUrl(rowData.licence_id)}>{rowInfo.value}</Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Application Date",
      accessor: "valid_application_date",
      Filter: false,
      Cell: (rowInfo: any) => formatDate(rowInfo.value),
    },
    {
      Header: "Site",
      accessor: "site_name",
      Filter: false,
    },
    {
      Header: "Local Authority",
      accessor: "local_authority_org_name",
      Filter: CreateStaticSelectColumnFilter(
        localAuthorityFilterValues.list.map((option) => option.local_authority)
      ),
      filter: "includes",
    },
    {
      Header: "Status",
      accessor: "licence_status",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.list.map((option) => option.status)
      ),
      filter: "includes",
    },
    {
      Header: "Waste Category",
      accessor: "waste_category",
      Filter: CreateStaticSelectColumnFilter(
        wasteCategoryFilterValues.list.map((option) => option.waste_category)
      ),
      filter: "includes",
    },
    {
      Header: "Main Class of Activity",
      accessor: "main_class_of_activity",
      Filter: CreateStaticSelectColumnFilter(
        classOfActivityFilterValues.list.map(
          (option) => option.class_of_activity
        )
      ),
      filter: "includes",
    },
  ];

  const tableColumnsSmall: Column<Record<string, ISearchResultRow>>[] = [
    {
      Header: "Reg Number",
      accessor: "reg_no",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link to={GetDetailsUrl(rowData.licence_id)}>{rowInfo.value}</Link>
        );
      },
      sortType: "basic",
    },
    {
      Header: "Application Date",
      accessor: "valid_application_date",
      Filter: false,
      Cell: (rowInfo: any) => formatDate(rowInfo.value),
    },
    {
      Header: "Site",
      accessor: "site_name",
      Filter: false,
    },
    {
      Header: "Local Authority",
      accessor: "local_authority_org_name",
      Filter: CreateStaticSelectColumnFilter(
        localAuthorityFilterValues.list.map((option) => option.local_authority)
      ),
      filter: "includes",
    },
    {
      Header: "Status",
      accessor: "licence_status",
      Filter: CreateStaticSelectColumnFilter(
        statusFilterValues.list.map((option) => option.status)
      ),
      filter: "includes",
    },
  ];

  const getFetchUrlFactory = () => {
    let fetchUrlFn = (
      pageNo: number,
      recordsPerPage: number,
      searchText: string,
      filters: any,
      orderBy?: EpaGridOrderByFetchParams
    ) => {
      return GetCertificatesOfRegistrationFetchUrl(
        pageNo,
        recordsPerPage,
        searchText,
        filters,
        orderBy
      );
    };

    return fetchUrlFn;
  };

  const fetchUrl = getFetchUrlFactory();

  const orderBy = {
    orderByAscending: false,
    orderByName: "Reg Number",
    enabledOrderByColumns: ["Reg Number", "Date Received", "Date Closed"],
  };

  const results = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetCertificatesOfRegistrationCsv}
        />
      </div>
      <div className="d-none d-md-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
          orderBy={orderBy}
          exportToCsvFunction={GetCertificatesOfRegistrationCsv}
        />
      </div>
    </div>
  );

  const contentElement = (
    <div>
      <Row>
        <Col className="ps-0">
          <EpaCard
            title="Search Certificates of Registration"
            content={results}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <div>
      <BreadCrumbs />
      <div className="ps-0">
        <Header />
        <CircularEconomyHomeContainer content={contentElement} />
      </div>
    </div>
  );
};

export default CertificatesOfRegistrationList;
